import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

export type OverflowNavItem = {
  to: string;
  label: string;
  /** The top-bar link exactly as the nav renders it. */
  node: ReactNode;
  badge?: number;
};

/** Least width kept free for the "More" button whenever something has to move into it (more with a badge). */
const MORE_RESERVE_PX = 112;

/**
 * Desktop top-bar links that never clip: every link is measured, the ones that fit stay in the bar in
 * order, and the rest move into a "More" menu at the end. Re-measured when the bar resizes, when web
 * fonts finish loading and whenever `remeasureKey` changes (route, badge counts), so a link is always
 * either fully visible or one click away in More.
 */
export default function OverflowNav({
  items,
  ariaLabel,
  className,
  isActive,
  remeasureKey,
  moreStyle,
}: {
  items: OverflowNavItem[];
  ariaLabel: string;
  className: string;
  isActive: (to: string) => boolean;
  remeasureKey?: string;
  moreStyle?: (active: boolean) => CSSProperties;
}) {
  const navRef = useRef<HTMLElement>(null);
  const [fit, setFit] = useState(items.length);
  // While More is open the bar is not re-measured, so the button the menu is anchored to never moves.
  const [moreOpen, setMoreOpen] = useState(false);
  const moreOpenRef = useRef(false);

  const measure = useCallback(() => {
    const nav = navRef.current;
    if (!nav || moreOpenRef.current) return;
    const els = Array.from(nav.querySelectorAll<HTMLElement>(':scope > [data-overflow-item]'));
    els.forEach((el) => { el.style.display = ''; });
    const moreBtn = nav.querySelector<HTMLElement>('[data-testid="nav-more"]');
    if (moreBtn) moreBtn.style.display = 'flex';
    const reserve = Math.max(MORE_RESERVE_PX, Math.ceil(moreBtn?.getBoundingClientRect().width ?? 0));
    if (moreBtn) moreBtn.style.display = '';
    const avail = nav.clientWidth;
    if (avail === 0) return; // nav hidden (mobile widths): nothing to decide
    const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
    const widths = els.map((el) => el.getBoundingClientRect().width);
    const total = widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, els.length - 1);
    let n = els.length;
    if (total > avail) {
      let used = 0;
      n = 0;
      for (const w of widths) {
        const next = used + (n > 0 ? gap : 0) + w;
        if (next + gap + reserve > avail) break;
        used = next;
        n += 1;
      }
    }
    els.forEach((el, i) => { el.style.display = i < n ? '' : 'none'; });
    setFit(n);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, items.length, remeasureKey]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    ro.observe(nav);
    void document.fonts?.ready.then(() => measure());
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [measure]);

  const overflow = items.slice(fit);
  const moreActive = overflow.some((it) => isActive(it.to));
  const moreBadge = overflow.reduce((sum, it) => sum + (it.badge && it.badge > 0 ? it.badge : 0), 0);

  return (
    <nav ref={navRef} className={className} aria-label={ariaLabel} data-testid="overflow-nav">
      {items.map((it) => (
        <div key={it.to} data-overflow-item className="shrink-0">
          {it.node}
        </div>
      ))}
      {/* Always mounted (hidden when nothing overflows) and non-modal: a modal menu locks page scroll,
          which changes the bar width, re-measures, and could swap out the anchor so the menu opened
          off-screen. */}
      <DropdownMenu
        modal={false}
        open={moreOpen}
        onOpenChange={(o) => {
          moreOpenRef.current = o;
          setMoreOpen(o);
          if (!o) requestAnimationFrame(measure);
        }}
      >
          <DropdownMenuTrigger asChild>
            {/* A native button: the shared Button does not forward refs (React 18), and without a ref
                the menu has no anchor and opens at the top-left corner, off-screen. */}
            <button
              type="button"
              className={`${overflow.length > 0 ? 'flex' : 'hidden'} h-9 shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg text-[13px] font-medium transition-all hover:bg-[var(--rr-red-light)] hover:text-[var(--text-brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rr-red)]`}
              style={{ padding: '6px 10px', ...(moreStyle ? moreStyle(moreActive) : {}) }}
              aria-current={moreActive ? 'page' : undefined}
              data-testid="nav-more"
            >
              More
              {moreBadge > 0 && <span className="ml-0.5 rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white">{moreBadge > 99 ? '99+' : moreBadge}</span>}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 rounded-xl">
            {overflow.map((it) => (
              <DropdownMenuItem key={it.to} asChild>
                <Link
                  to={it.to}
                  data-testid="nav-more-item"
                  aria-current={isActive(it.to) ? 'page' : undefined}
                  className={isActive(it.to) ? 'font-semibold text-[var(--text-brand)]' : undefined}
                >
                  {it.label}
                  {it.badge && it.badge > 0 ? <span className="ml-auto rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white">{it.badge > 99 ? '99+' : it.badge}</span> : null}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}
